import mime from 'mime-types';

/**
 * Custom extension-to-MIME mappings for non-standard types not in mime-db.
 */
const CUSTOM_EXTENSION_MAP: Record<string, string> = {
  '.ts': 'application/typescript',
  '.py': 'text/x-python',
  '.rs': 'text/x-rust',
  '.pbix': 'application/x-powerbi',
  '.fig': 'application/x-figma',
  '.parquet': 'application/vnd.apache.parquet',
  '.db': 'application/vnd.sqlite3',
  '.sqlite': 'application/vnd.sqlite3'
};

/**
 * Windows browsers often report non-standard MIME types from the OS registry.
 * This map normalizes them to their standard equivalents.
 */
const WINDOWS_MIME_NORMALIZATION: Record<string, string> = {
  'application/x-zip-compressed': 'application/zip',
  'application/x-gzip': 'application/gzip',
  'application/x-sqlite3': 'application/vnd.sqlite3'
};

/**
 * Resolves the content type for a file, working consistently across all operating systems.
 *
 * On Windows, the browser's `file.type` relies on the OS registry which often returns
 * empty strings for unregistered extensions (.parquet, .tsv, .ipynb) or non-standard
 * variants (e.g. `application/x-zip-compressed` instead of `application/zip`).
 *
 * This function uses the `mime-types` library (backed by mime-db) for OS-agnostic
 * extension-based detection, with fallbacks for custom types and Windows normalization.
 *
 * @param fileName - The file name including extension
 * @param browserMime - The MIME type reported by the browser (file.type)
 * @returns The resolved MIME type, or empty string if unresolvable
 */
export function resolveContentType(fileName: string, browserMime: string): string {
  const ext = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  const customMime = CUSTOM_EXTENSION_MAP[ext];
  if (customMime) return customMime;

  const mimeFromExtension = mime.lookup(fileName);
  if (mimeFromExtension) {
    return mimeFromExtension;
  }

  const effectiveMime = browserMime === 'application/octet-stream' ? '' : browserMime;
  const normalized = WINDOWS_MIME_NORMALIZATION[effectiveMime] || effectiveMime;
  return normalized || '';
}

/**
 * Expands an array of allowed MIME types to include file extensions and known
 * Windows MIME variants. This ensures Uppy's file restriction doesn't reject
 * files on Windows before our custom validation runs.
 *
 * @param allowedTypes - Array of standard MIME types (e.g. ['application/zip'])
 * @returns Expanded array including extensions and Windows variants
 */
// Pre-built reverse lookups for expandAllowedFileTypes (derived from static maps)
const REVERSE_NORMALIZATION: Record<string, string[]> = {};
for (const [windowsMime, standardMime] of Object.entries(WINDOWS_MIME_NORMALIZATION)) {
  if (!REVERSE_NORMALIZATION[standardMime]) {
    REVERSE_NORMALIZATION[standardMime] = [];
  }
  REVERSE_NORMALIZATION[standardMime].push(windowsMime);
}

const REVERSE_CUSTOM: Record<string, string[]> = {};
for (const [ext, mimeType] of Object.entries(CUSTOM_EXTENSION_MAP)) {
  if (!REVERSE_CUSTOM[mimeType]) {
    REVERSE_CUSTOM[mimeType] = [];
  }
  REVERSE_CUSTOM[mimeType].push(ext);
}

export function expandAllowedFileTypes(allowedTypes: string[]): string[] {
  if (!allowedTypes || allowedTypes.length === 0) return allowedTypes;

  const expanded = new Set<string>(allowedTypes);

  for (const mimeType of allowedTypes) {
    const ext = mime.extension(mimeType);
    if (ext) {
      expanded.add(`.${ext}`);
    }

    const customExts = REVERSE_CUSTOM[mimeType];
    if (customExts) {
      customExts.forEach((e) => expanded.add(e));
    }

    const windowsVariants = REVERSE_NORMALIZATION[mimeType];
    if (windowsVariants) {
      windowsVariants.forEach((v) => expanded.add(v));
    }
  }

  return Array.from(expanded);
}

/**
 * Magic byte signatures for binary file type detection.
 * Each entry: [byte pattern, offset, MIME type].
 * Patterns use -1 as a wildcard byte (matches anything).
 */
const MAGIC_SIGNATURES: [number[], number, string][] = [
  // Images
  [[0x89, 0x50, 0x4e, 0x47], 0, 'image/png'],
  [[0xff, 0xd8, 0xff], 0, 'image/jpeg'],
  [[0x47, 0x49, 0x46, 0x38], 0, 'image/gif'],
  // Audio
  [[0x49, 0x44, 0x33], 0, 'audio/mpeg'], // ID3 tag
  [[0xff, 0xfb], 0, 'audio/mpeg'], // MP3 sync
  [[0xff, 0xf3], 0, 'audio/mpeg'],
  [[0xff, 0xf2], 0, 'audio/mpeg'],
  [[0x4f, 0x67, 0x67, 0x53], 0, 'audio/ogg'],
  [[0x52, 0x49, 0x46, 0x46, -1, -1, -1, -1, 0x57, 0x41, 0x56, 0x45], 0, 'audio/wav'],
  // Video
  [[-1, -1, -1, -1, 0x66, 0x74, 0x79, 0x70], 0, 'video/mp4'], // ftyp at bytes 4-7
  [[0x1a, 0x45, 0xdf, 0xa3], 0, 'video/webm'],
  // Documents
  [[0x25, 0x50, 0x44, 0x46], 0, 'application/pdf'],
  [[0xd0, 0xcf, 0x11, 0xe0], 0, 'application/msword'],
  // Archives
  [[0x50, 0x4b, 0x03, 0x04], 0, 'application/zip'],
  [[0x1f, 0x8b], 0, 'application/gzip'],
  [[0x37, 0x7a, 0xbc, 0xaf, 0x27, 0x1c], 0, 'application/x-7z-compressed'],
  [[0x52, 0x61, 0x72, 0x21], 0, 'application/x-rar-compressed'],
  // Database
  [
    [
      0x53, 0x51, 0x4c, 0x69, 0x74, 0x65, 0x20, 0x66, 0x6f, 0x72, 0x6d, 0x61, 0x74, 0x20, 0x33, 0x00
    ],
    0,
    'application/vnd.sqlite3'
  ]
];

/**
 * Detects MIME type from file content using magic bytes.
 * Only needs the first ~16 bytes of the file.
 *
 * @param buffer - The file header bytes as ArrayBuffer or Uint8Array
 * @returns The detected MIME type, or empty string if undetectable
 */
export function resolveContentTypeFromBuffer(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  if (bytes.length === 0) return '';

  for (const [pattern, offset, mimeType] of MAGIC_SIGNATURES) {
    if (bytes.length < offset + pattern.length) continue;
    let match = true;
    for (let i = 0; i < pattern.length; i++) {
      if (pattern[i] !== -1 && bytes[offset + i] !== pattern[i]) {
        match = false;
        break;
      }
    }
    if (match) return mimeType;
  }

  return '';
}

/**
 * Resolves content type with magic-byte fallback.
 *
 * Tries sync extension-based resolution first. If that returns empty
 * (e.g. unknown extension + octet-stream browser MIME), falls back to
 * reading magic bytes from the file buffer.
 *
 * @param fileName - The file name including extension
 * @param browserMime - The MIME type reported by the browser (file.type)
 * @param fileBuffer - The file content for magic-byte detection, or a function that returns it lazily
 * @returns The resolved MIME type, or empty string if unresolvable
 */
export async function resolveContentTypeWithMagic(
  fileName: string,
  browserMime: string,
  fileBuffer: ArrayBuffer | Uint8Array | (() => Promise<ArrayBuffer>)
): Promise<string> {
  const syncResult = resolveContentType(fileName, browserMime);
  if (syncResult) return syncResult;
  const buffer = typeof fileBuffer === 'function' ? await fileBuffer() : fileBuffer;
  return resolveContentTypeFromBuffer(buffer);
}
